﻿import { createStyles, withStyles } from '@mui/styles'
import {
    Dialog, DialogContent, DialogActions, Typography, Table, TableHead, TableRow, TableCell, TableBody, Checkbox
} from '@mui/material'
import { Add, Delete, FileCopy } from '@mui/icons-material'
import React, { RefObject, useEffect, useImperativeHandle, useRef, useState } from 'react'
import { MuiProps, defaultStyles, muiOptions } from '../../infrastructure/materialUiThemeProvider'
import { Button, CustomDialog, NumberField, Select, Switch } from '../common/customComponents'
import { t } from '../../infrastructure/i18nextHelper'
import { MktSalesContainer } from './mktSalesStore'
import { Company, CustomerSegment, MktSalesDemandPlanChange, defaultDemandPlanChange } from './models'
import moment from 'moment'
import { api } from '../../infrastructure/api'
import { snackbars } from '../../infrastructure/snackbars'
import { isoDateFormat } from '../../infrastructure/date'
import guid, { Guid } from '../../infrastructure/guid'
import { DatePicker } from '../common/customComponents'
import { CommentButton } from '../common/comment/commentButton'
import { CommentContainer } from '../common/comment/commentStore'
import { MktSalesFiltersContainer } from './mktSalesFiltersStore'

let dialogRef: RefObject<{ open: () => void }> | null = null
type MktSalesDemandPlanChangeKeys = keyof MktSalesDemandPlanChange

function _MktSaleDemandPlan({ classes }: MuiProps) {
    let [demandPlanCount, setDemandPlanCount] = useState<number>(0)
    let store = MktSalesContainer.useContainer()

    useEffect(() => {
        let count = store.mktSaleDates.flatMap(x => x.movements).filter(x => x.hasDemandPlanChange).length
        setDemandPlanCount(count)
    }, [store.mktSaleDates])

    return (
        <>
            <div className={classes.container} onClick={() => dialogRef?.current?.open()}>
                <div className={classes.title}>{t('mktSales.demandPlan.title')}</div>
                <Button className={classes.confirmButton + ' ' + classes.restrictionButton}
                    label={<span className={classes.restrictionButtonText}>
                        <Typography variant='caption' className={classes.secondaryText}>
                            {demandPlanCount > 0
                                ? t('mktSales.demandPlan.existingChanges')
                                : t('mktSales.demandPlan.noChange')
                            }
                        </Typography>
                    </span>}
                    color='primary' />
            </div>
            <MktSaleDemandPlanDialog classes={classes} />
        </>
    )
}

function _MktSaleDemandPlanDialog({ classes }: MuiProps) {
    let [isOpen, setIsOpen] = useState<boolean>(false)
    let [demandPlanChanges, setDemandPlanChanges] = useState<MktSalesDemandPlanChange[]>([])
    let [volumeValueOriginByDemandId, setVolumeValueOriginByDemandId] = useState<Record<string, 'api' | 'user'>>({})
    let [selectedDemandPlanIds, setSelectedDemandPlanIds] = useState<string[]>([])
    let [companys, setCompanys] = useState<Company[]>([])
    let [showConfirmSave, setShowConfirmSave] = useState<boolean>(false)
    let [showConfirmUnsavedChanges, setShowConfirmUnsavedChanges] = useState<boolean>(false)
    let store = MktSalesContainer.useContainer()
    let filterStore = MktSalesFiltersContainer.useContainer()
    let commentStore = CommentContainer.useContainer()

    dialogRef = useRef<{ open: () => void }>(null)

    useImperativeHandle(dialogRef, () => ({
        open: () => setIsOpen(true)
    }))

    useEffect(() => {
        setCompanyValues()
    }, [filterStore.companiesFilter.items, filterStore.customerSegmentsFilter.items])

    let setCompanyValues = () => {
        let customerSegments = filterStore.customerSegmentsFilter.items
            .groupBy(x => x.company, x => x)
            .map(([key, items]) => ({
                company: key,
                customerSegments: items.flatMap(x => x.customerSegment).distinct()
            }))

        let companys = filterStore.companiesFilter.items
            .map(x => ({
                ...x,
                customerSegments: customerSegments.find(y => y.company === x.code)?.customerSegments ?? []
            }))

        setCompanys(companys)
    }

    let getDefaultCompany = () => filterStore.companiesFilter.items[0]?.code ?? null

    let addNewLine = () => {
        let newArray = [...demandPlanChanges]
        let tomorrow = moment().add(1, 'days').toISOString()
        let defaultCompany = getDefaultCompany()

        newArray.push({
            ...defaultDemandPlanChange(),
            id: guid.createNew(),
            company: defaultCompany,
            dateFrom: tomorrow,
            dateTo: tomorrow
        })

        setDemandPlanChanges(newArray)
    }

    let duplicateLine = async () => {
        if (selectedDemandPlanIds.length !== 1)
            return

        let selectedDemand = demandPlanChanges.find(x => x.id === selectedDemandPlanIds[0])
        if (!selectedDemand)
            return

        let duplicatedDemand = {
            ...selectedDemand,
            id: guid.createNew()
        }

        await api.post(`stock/movement/mktsale/duplicateDemandPlanChangeComment`,
            { 'sourceId': selectedDemand.id, 'destinationId': duplicatedDemand.id })

        let newArray = [...demandPlanChanges, duplicatedDemand]
        setDemandPlanChanges(newArray)
    }

    let deleteLines = async () => {
        if (!selectedDemandPlanIds.length)
            return

        let newArray = [...demandPlanChanges]
            .filter(x => !selectedDemandPlanIds.includes(x.id))

        await deleteComments(selectedDemandPlanIds)
        setDemandPlanChanges(newArray)
        setSelectedDemandPlanIds([])
    }

    let deleteComments = async (ids: Guid[]) => {
        await api.del('stock/movement/mktsale/deleteDemandPlanChangeComment', { ids })
    }


    let isValidPeriod = (start: string, end: string) => {
        let dateFrom = moment(start)
        let dateTo = moment(end)

        return dateFrom.isValid() && dateTo.isValid() && dateFrom.isSameOrBefore(dateTo)
    }

    let isValidPerimeter = (demand: MktSalesDemandPlanChange) => !!demand.productId && !!demand.site

    let getForecastPeriod = (demand: MktSalesDemandPlanChange): { start: string, end: string } => {
        if (demand.takeForecastValue)
            return {
                start: moment(demand.dateFrom).format(isoDateFormat),
                end: moment(demand.dateTo).format(isoDateFormat)
            }

        return {
            start: moment(demand.dateFrom).subtract(7, 'days').format(isoDateFormat),
            end: moment(demand.dateFrom).subtract(1, 'days').format(isoDateFormat)
        }
    }

    let computeDays = (demand: MktSalesDemandPlanChange) => {
        let days = moment(demand.dateTo).diff(moment(demand.dateFrom), 'days') + 1
        setValue(demand, 'days', days)
    }

    let computeWeightedDays = async (demand: MktSalesDemandPlanChange) => {
        let customerSegmentProducts = await api.get<CustomerSegment[]>(
            `stock/movement/mktsale/customerSegmentProduct/${demand.company}/${demand.dateFrom}/${demand.dateTo}`)

        let matchingSegments = customerSegmentProducts
            .filter(x => (!demand.company || demand.company === x.company)
                && (!demand.customerSegment || demand.customerSegment === x.code))

        let segment = matchingSegments.find(x => !demand.site || demand.site === x.site) ?? matchingSegments[0]

        let totalWeight = segment?.products
            .find(x => (!demand.productId || demand.productId === x.id))?.periodCoeff ?? 0

        setValue(demand, 'weightedDays', totalWeight)
    }

    let changeOriginOfValue = (demand: MktSalesDemandPlanChange) => {
        setValue(demand, 'takeForecastValue', !demand.takeForecastValue)
        computeVolumes(demand)
    }

    let computeVolumes = async (demand: MktSalesDemandPlanChange) => {
        let period = getForecastPeriod(demand)

        let totalOfQuantity = await api.get<number>(`stock/movement/mktsale/totalQuantity`, {
            dateFrom: period.start,
            dateTo: period.end,
            productId: demand.productId,
            site: demand.site,
            company: demand.company,
            customerSegment: demand.customerSegment,
            takeForecast: demand.takeForecastValue
        })

        let divisor = demand.takeForecastValue ? demand.weightedDays : 7
        if (divisor)
            setVolumePerDay(demand, Math.round(totalOfQuantity / divisor), true)
        else
            setVolumePerDay(demand, 0, true)
    }

    let setTotalVolume = (demand: MktSalesDemandPlanChange, value: number | null) => {
        setVolumeValueOriginByDemandId(previous => ({ ...previous, [demand.id]: 'user' }))
        let volume = value === null || isNaN(value) ? 0 : +value
        setValue(demand, 'newTotalPlannedVolume', volume)

        if (!!demand.weightedDays) {
            let volumePerDay = Math.round(volume / demand.weightedDays)
            setValue(demand, 'newPlannedVolumePerDay', volumePerDay)
            if (demand.takeForecastValue)
                updatePercentage(demand, volumePerDay)
        }
    }

    let setVolumePerDay = (demand: MktSalesDemandPlanChange, value: number | null, setOldTotalVolume: boolean = false) => {
        setVolumeValueOriginByDemandId(previous => ({ ...previous, [demand.id]: setOldTotalVolume ? 'api' : 'user' }))
        let volume = value === null || isNaN(value) ? 0 : +value
        setValue(demand, 'newPlannedVolumePerDay', volume)

        let totalVolume = Math.round(volume * demand.weightedDays)
        setValue(demand, 'newTotalPlannedVolume', totalVolume)

        if (setOldTotalVolume && demand.takeForecastValue) {
            setValue(demand, 'originalPlannedVolumePerDay', volume)
            setValue(demand, 'percentagePlannedVolume', 100)
        }
        else if (demand.takeForecastValue)
            updatePercentage(demand, volume)

        if (setOldTotalVolume)
            setValue(demand, 'oldTotalPlannedVolume', totalVolume)
    }

    let setPercentage = (demand: MktSalesDemandPlanChange, value: number | null) => {
        let percentage = value === null || isNaN(value) ? 0 : +value
        setValue(demand, 'percentagePlannedVolume', percentage)

        if (demand.takeForecastValue) {
            let volumePerDay = demand.originalPlannedVolumePerDay * percentage / 100
            setVolumePerDay(demand, volumePerDay)
        }
    }

    let updatePercentage = (demand: MktSalesDemandPlanChange, volumePerDay: number) => {
        let percentage = demand.originalPlannedVolumePerDay
            ? volumePerDay / demand.originalPlannedVolumePerDay * 100
            : 0
        setValue(demand, 'percentagePlannedVolume', percentage)
    }

    let isValidRow = (demand: MktSalesDemandPlanChange, index: number) => {
        let isValid = true
        let indexText = { index: `#${++index}` }

        if (!isValidPeriod(demand.dateFrom, demand.dateTo)) {
            snackbars.warning(t('mktSales.demandPlan.dialog.messages.invalidDates', indexText))
            isValid = false
        }

        if (!demand.productId || demand.productId === guid.empty) {
            snackbars.warning(t('mktSales.demandPlan.dialog.messages.invalidProduct', indexText))
            isValid = false
        }

        if (!demand.site) {
            snackbars.warning(t('mktSales.demandPlan.dialog.messages.invalidSite', indexText))
            isValid = false
        }

        let hasDuplicatePeriod = demandPlanChanges.some(existing => existing.id !== demand.id
            && existing.company === demand.company
            && existing.productId === demand.productId
            && existing.site === demand.site
            && existing.customerSegment === demand.customerSegment
            && isValidPeriod(existing.dateFrom, existing.dateTo)
            && moment(demand.dateFrom).isSameOrBefore(existing.dateTo, 'day')
            && moment(demand.dateTo).isSameOrAfter(existing.dateFrom, 'day'))

        if (hasDuplicatePeriod) {
            snackbars.warning(t('mktSales.demandPlan.dialog.messages.duplicatePeriod', indexText))
            isValid = false
        }

        if (!commentStore.existingKeys.has(getAssociationKey(demand))) {
            snackbars.warning(t('mktSales.demandPlan.dialog.messages.emptyComment', indexText))
            isValid = false
        }

        return isValid
    }

    let setValue = <K extends MktSalesDemandPlanChangeKeys>(demand: MktSalesDemandPlanChange, prop: K, value) => {
        demand[prop] = value
        let newArray = [...demandPlanChanges].map(x => x.id === demand.id ? demand : x)
        setDemandPlanChanges(newArray)
    }

    let setAndComputeValues = async <K extends MktSalesDemandPlanChangeKeys>(demand: MktSalesDemandPlanChange, prop: K, value: string | null) => {
        setValue(demand, prop, value)

        if (isValidPeriod(demand.dateFrom, demand.dateTo)) {
            computeDays(demand)
            await computeWeightedDays(demand)

            if (isValidPerimeter(demand))
                computeVolumes(demand)
        }
    }

    let setDemandDateFrom = (demand: MktSalesDemandPlanChange, newDate: string | null) => {
        setAndComputeValues(demand, 'dateFrom', newDate)

        if (!demand.dateTo || moment(newDate).isAfter(demand.dateTo))
            setAndComputeValues(demand, 'dateTo', newDate)
    }

    let setDemandDateTo = (demand: MktSalesDemandPlanChange, newDate: string | null) => {
        setAndComputeValues(demand, 'dateTo', newDate)

        if (!demand.dateFrom || (moment(newDate).isBefore(demand.dateFrom)))
            setAndComputeValues(demand, 'dateFrom', newDate)
    }

    let setCompany = (demand: MktSalesDemandPlanChange, company: string | null) => {
        setAndComputeValues(demand, 'company', company)
        setAndComputeValues(demand, 'customerSegment', null)
    }

    let getCustomerSegmentsChoices = (demand: MktSalesDemandPlanChange) => {
        let choices = companys.find(x => x.code === demand.company)?.customerSegments ?? []
        return choices.map(x => ({ value: x, text: x }))
    }

    let closeMktSaleDemandPlanDialog = () => {
        setSelectedDemandPlanIds([])
        setDemandPlanChanges([])
        setVolumeValueOriginByDemandId({})
        setIsOpen(false)
    }

    let closeConfirmUnsavedChangesDialog = async () => {
        await deleteComments(demandPlanChanges.map(x => x.id))
        setShowConfirmUnsavedChanges(false)
        closeMktSaleDemandPlanDialog()
    }

    let checkUnsavedChanges = () => {
        let hasUnsavedChanges = demandPlanChanges.length > 0
        if (hasUnsavedChanges) {
            setShowConfirmUnsavedChanges(true)
            return
        }
        closeMktSaleDemandPlanDialog()
    }

    let validateChanges = () => {
        if (demandPlanChanges.some((x, index) => !isValidRow(x, index)))
            return

        setShowConfirmSave(true)
    }

    let saveChanges = async () => {
        try {
            await api.post(`stock/movement/mktsale/demandPlanChange`, { demandPlanChanges })
            setShowConfirmSave(false)
            closeMktSaleDemandPlanDialog()
            snackbars.success(t('mktSales.demandPlan.dialog.messages.savedSuccess'))
            await store.loadMktSaleMovements()
        }
        catch (err) {
            setShowConfirmSave(false)
        }
    }

    let getAssociationKey = (demand: MktSalesDemandPlanChange) => `demand-plan-change-${demand.id}`

    let toggleSelectLine = (demandId: string) => {
        setSelectedDemandPlanIds(previous => previous.includes(demandId)
            ? previous.filter(id => id !== demandId)
            : [...previous, demandId])
    }

    let toggleSelectAllLines = () => {
        let allSelected = demandPlanChanges.length > 0
            && demandPlanChanges.every(x => selectedDemandPlanIds.includes(x.id))

        setSelectedDemandPlanIds(allSelected ? [] : demandPlanChanges.map(x => x.id))
    }

    let isAllLinesSelected = demandPlanChanges.length > 0
        && demandPlanChanges.every(x => selectedDemandPlanIds.includes(x.id))

    return (
        <>
            <Dialog
                open={isOpen}
                onClose={() => { }}
                scroll={'paper'}
                aria-labelledby='scroll-dialog-title'
                aria-describedby='scroll-dialog-description'
                fullWidth={true}
                maxWidth={'xl'}
                classes={{ paper: classes.dialog }}>
                <DialogContent>
                    <div className={classes.dialogTitle}>
                        <div>
                            <Typography variant='h5'>{t('mktSales.demandPlan.dialog.demandManagementRules')}</Typography>
                            <Typography className={classes.titleExplanation} variant='subtitle1'>{t('mktSales.demandPlan.dialog.titleExplanation')}</Typography>
                        </div>
                        <div className={classes.actionButtons}>
                            <Button
                                onClick={addNewLine}
                                label={t('mktSales.demandPlan.dialog.addNewLine')}
                                img={<Add />}
                                className={classes.addNewLineButton} />
                            <Button
                                onClick={duplicateLine}
                                disabled={selectedDemandPlanIds.length !== 1}
                                label={t('mktSales.demandPlan.dialog.duplicateLine')}
                                img={<FileCopy />}
                                className={classes.duplicateLineButton} />
                            <Button
                                onClick={deleteLines}
                                disabled={!selectedDemandPlanIds.length}
                                label={t('mktSales.demandPlan.dialog.deleteLine')}
                                img={<Delete />}
                                className={classes.removeLineButton} />
                        </div>
                    </div>
                    <div className={classes.fullTableContainer}>
                        <Table className={classes.table} aria-label='table'>
                            <TableHead className={classes.thead}>
                                <TableRow className={classes.row} key={"header"}>
                                    <TableCell padding='checkbox'>
                                        <Checkbox
                                            checked={isAllLinesSelected}
                                            indeterminate={selectedDemandPlanIds.length > 0 && !isAllLinesSelected}
                                            onChange={toggleSelectAllLines} />
                                    </TableCell>
                                    <TableCell>{t('mktSales.demandPlan.dialog.dateFrom')}</TableCell>
                                    <TableCell>{t('mktSales.demandPlan.dialog.dateTo')}</TableCell>
                                    <TableCell align={'center'}>{t('mktSales.demandPlan.dialog.days')}</TableCell>
                                    <TableCell align={'center'}>{t('mktSales.demandPlan.dialog.weightDays')}</TableCell>
                                    <TableCell>{t('mktSales.demandPlan.dialog.company')}</TableCell>
                                    <TableCell>{t('mktSales.demandPlan.dialog.product')}</TableCell>
                                    <TableCell>{t('mktSales.demandPlan.dialog.site')}</TableCell>
                                    <TableCell>{t('mktSales.demandPlan.dialog.customerSegment')}</TableCell>
                                    <TableCell align="center">{t('mktSales.demandPlan.dialog.originOfValue')}</TableCell>
                                    {demandPlanChanges.some(x => x.takeForecastValue) &&
                                        <TableCell align={'right'} className={classes.cellPaddingRight}>{t('mktSales.demandPlan.dialog.percentage')}</TableCell>}
                                    <TableCell align={'right'} className={classes.cellPaddingRight}>{t('mktSales.demandPlan.dialog.plannedVolPerDay')}</TableCell>
                                    <TableCell align={'right'} className={classes.cellPaddingRight}>{t('mktSales.demandPlan.dialog.plannedTotalVol')}</TableCell>
                                    <TableCell align="center">{t('mktSales.demandPlan.dialog.comment')}</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {demandPlanChanges.map(demand =>
                                    <TableRow className={classes.row} key={demand.id}>
                                        <TableCell padding='checkbox'>
                                            <Checkbox
                                                checked={selectedDemandPlanIds.includes(demand.id)}
                                                onChange={() => toggleSelectLine(demand.id)} />
                                        </TableCell>
                                        <TableCell className={classes.mediumCell}>
                                            <DatePicker
                                                disableNewStyle
                                                date={demand.dateFrom}
                                                setDate={newDate => setDemandDateFrom(demand, newDate)}
                                                label={''}
                                                smallIcon={true}
                                                classesOverride={{ datepicker: classes.smallSize }} />
                                        </TableCell>
                                        <TableCell className={classes.mediumCell}>
                                            <DatePicker
                                                disableNewStyle
                                                date={demand.dateTo}
                                                setDate={newDate => setDemandDateTo(demand, newDate)}
                                                label={''}
                                                smallIcon={true}
                                                classesOverride={{ datepicker: classes.smallSize }} />
                                        </TableCell>
                                        <TableCell className={classes.smallCell} align={'center'}>{demand.days}</TableCell>
                                        <TableCell className={classes.smallCell} align={'center'}>{demand.weightedDays}</TableCell>
                                        <TableCell className={classes.mediumCell}>
                                            <Select variant={'standard'} isClearable
                                                label={''}
                                                value={demand.company}
                                                choices={companys.map(x => ({ value: x.code, text: x.name }))}
                                                onChange={x => setCompany(demand, x)}
                                                classesOverride={{ form: classes.smallCell }} />
                                        </TableCell>
                                        <TableCell className={classes.mediumCell}>
                                            <Select variant={'standard'}
                                                label={''}
                                                value={demand.productId}
                                                choices={filterStore.productIdsFilter.choices}
                                                onChange={x => setAndComputeValues(demand, 'productId', x)}
                                                classesOverride={{ form: classes.mediumCell }} />
                                        </TableCell>
                                        <TableCell className={classes.mediumCell}>
                                            <Select variant={'standard'}
                                                label={''}
                                                value={demand.site}
                                                choices={filterStore.sitesFilter.choices}
                                                onChange={x => setAndComputeValues(demand, 'site', x)}
                                                classesOverride={{ form: classes.mediumCell }} />
                                        </TableCell>
                                        <TableCell className={classes.mediumCell}>
                                            <Select variant={'standard'} isClearable
                                                label={''}
                                                value={demand.customerSegment}
                                                choices={getCustomerSegmentsChoices(demand)}
                                                onChange={x => setAndComputeValues(demand, 'customerSegment', x)}
                                                classesOverride={{ form: classes.mediumCell }} />
                                        </TableCell>
                                        <TableCell className={classes.largeCell}>
                                            <Switch form
                                                isChecked={demand.takeForecastValue}
                                                changeCallback={() => changeOriginOfValue(demand)}
                                                className={classes.switch}
                                                offText={t('mktSales.demandPlan.dialog.lastSevenDays')}
                                                onText={t('mktSales.demandPlan.dialog.forecast')} />
                                        </TableCell>
                                        {demandPlanChanges.some(x => x.takeForecastValue) &&
                                            <TableCell className={classes.smallCell} align={'right'}>
                                                {demand.takeForecastValue &&
                                                    <NumberField disableNewStyle
                                                        onChange={x => setPercentage(demand, x)}
                                                        size={'small'}
                                                        text={demand.percentagePlannedVolume}
                                                        precision={3}
                                                        align={'right'} />
                                                }
                                            </TableCell>
                                        }
                                        <TableCell className={classes.smallCell} align={'right'}>
                                            <NumberField disableNewStyle
                                                onChange={x => setVolumePerDay(demand, x)}
                                                size={'small'}
                                                text={demand.newPlannedVolumePerDay}
                                                italic={volumeValueOriginByDemandId[demand.id] === 'api'}
                                                precision={3}
                                                align={'right'} />
                                        </TableCell>
                                        <TableCell className={classes.smallCell} align={'right'}>
                                            <NumberField disableNewStyle
                                                onChange={x => setTotalVolume(demand, x)}
                                                size={'small'}
                                                text={demand.newTotalPlannedVolume}
                                                italic={volumeValueOriginByDemandId[demand.id] === 'api'}
                                                precision={3}
                                                align={'right'} />
                                        </TableCell>
                                        <TableCell className={classes.smallCell} align="center">
                                            <CommentButton associationKey={getAssociationKey(demand)} />
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </div>
                </DialogContent>
                <DialogActions>
                    <Button className={classes.closeButton}
                        label={t('comment.closeButton')}
                        onClick={checkUnsavedChanges}
                        color='primary' />
                    <Button className={classes.saveButton}
                        disabled={!demandPlanChanges.length}
                        label={t('comment.saveButton')}
                        onClick={validateChanges}
                        color='primary' />
                </DialogActions>
            </Dialog >

            <CustomDialog isOpen={showConfirmSave}
                title={t('mktSales.demandPlan.dialog.confirmation')}
                contentText={t('mktSales.demandPlan.dialog.confirmSave')}
                confirmButtonText={t('components.dialogYes')}
                onConfirm={saveChanges}
                onClose={() => { }}
                onCancel={() => setShowConfirmSave(false)} />

            <CustomDialog isOpen={showConfirmUnsavedChanges}
                title={t('mktSales.demandPlan.dialog.confirmation')}
                contentText={t('mktSales.demandPlan.dialog.confirmUnsavedChanges')}
                confirmButtonText={t('components.dialogYes')}
                onConfirm={closeConfirmUnsavedChangesDialog}
                onClose={() => { }}
                onCancel={() => setShowConfirmUnsavedChanges(false)} />
        </>)
}
let styles = () =>
    createStyles({
        container: {
            padding: '0 1em',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            height: '100%',
            cursor: 'pointer'
        },
        title: {
            color: 'rgba(0, 0, 0, 0.54)',
            fontSize: '0.8em',
            fontFamily: "\"Roboto\", \"Helvetica\", \"Arial\", sans-serif",
            marginTop: '0.5em'
        },
        titleExplanation: {
            padding: 0,
            margin: 0,
            fontStyle: 'italic',
            fontWeight: 'normal'
        },
        table: {
            border: '1px solid #ddd',
            marginTop: '1em'
        },
        fullTableContainer: { minWidth: '104em' },
        smallSize: {
            width: '12em',
            minWidth: '12em',
            flexShrink: 0,
            overflow: 'visible',
        },
        noMarginTop: { marginTop: '0 !important' },
        row: {
            "& td": { padding: '0.5em' },
            "& td:first-child, th:first-child": { paddingLeft: '0.9em' },
            "& th": { padding: '0.8em 0.3em 0.8em 0.3em' },
            "&:hover": { backgroundColor: '#fafafa' },
        },
        thead: { backgroundColor: '#fafafa' },
        switch: {
            ...defaultStyles.flexRow,
            justifyContent: 'start',
            minWidth: '0'
        },
        largeCell: { width: '15em' },
        mediumCell: { width: '10em' },
        smallCell: { width: '4em' },
        cellPaddingRight: { paddingRight: '1em !important' },
        commentIcon: {
            height: '1em',
            width: '1em',
        },
        addNewLineButton: {
            ...defaultStyles.primaryButton
        },
        actionButtons: {
            ...defaultStyles.flexRow,
            gap: '1.5em',
            alignItems: 'center'
        },
        dialogTitle: {
            ...defaultStyles.flexRow,
            justifyContent: 'space-between',
            "& h2": { margin: '0 0 0.1em' },
            "& h6": { fontSize: 'medium' },
            position: 'sticky',
            left: '0'
        },
        dialog: {
            position: 'absolute',
            top: 0,
            left: 0
        }
    })

export let MktSaleDemandPlan = withStyles(styles, muiOptions)(_MktSaleDemandPlan)
export let MktSaleDemandPlanDialog = withStyles(styles, muiOptions)(_MktSaleDemandPlanDialog)